import { Timeseries } from "../types";
import { computeAnomalies, AnomalyType, AnomalyRange } from "./anomalies";

export interface EwmaOptions {
  lambda: number;
  limitMultiplier: number;
}

export interface EwmaSeries {
  timestamps: Date[];
  values: number[];
  centerLine: number[];
  upperControl: number[];
  lowerControl: number[];
  anomalies: AnomalyRange[];
}

export function computeEwma(ts: Timeseries, options: EwmaOptions): EwmaSeries {
  const lambda = clamp(options.lambda ?? 0.2, 0.01, 1);
  const L = options.limitMultiplier ?? 3;

  const timestamps = ts.timestamps;
  const values = ts.data;
  if (timestamps.length === 0) {
    return {
      timestamps: [],
      values: [],
      centerLine: [],
      upperControl: [],
      lowerControl: [],
      anomalies: [],
    };
  }

  const target = mean(values);
  const sigma = standardDeviation(values);
  const sigmaE = sigma * Math.sqrt(lambda / (2 - lambda));
  const ewmaValues: number[] = [];
  const upper: number[] = [];
  const lower: number[] = [];

  let prev = target;
  for (let i = 0; i < values.length; i++) {
    const current = lambda * values[i] + (1 - lambda) * prev;
    ewmaValues.push(current);
    // Time-varying control limits for EWMA: widen over time until asymptote
    // sd_t = sigmaE * sqrt(1 - (1 - lambda)^(2t)), where t starts at 1
    const t = i + 1;
    const timeFactor = Math.sqrt(1 - Math.pow(1 - lambda, 2 * t));
    const limit = L * sigmaE * timeFactor;
    upper.push(target + limit);
    lower.push(target - limit);
    prev = current;
  }

  const supportedAnomalyTypes: AnomalyType[] = ["outside_limits"];
  const anomalies = computeAnomalies(supportedAnomalyTypes, ts.timestamps, ewmaValues, target, sigmaE, lower, upper);

  return {
    timestamps,
    values: ewmaValues,
    centerLine: ewmaValues.map(() => target),
    upperControl: upper,
    lowerControl: lower,
    anomalies,
  };
}

function mean(values: number[]): number {
  if (values.length === 0) return 0;
  const total = values.reduce((sum, value) => sum + value, 0);
  return total / values.length;
}

function standardDeviation(values: number[]): number {
  if (values.length < 2) return 0;
  const avg = mean(values);
  const variance = values.reduce((sum, value) => sum + Math.pow(value - avg, 2), 0) / (values.length - 1);
  return Math.sqrt(Math.max(variance, 0));
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}
